import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import find from 'lodash/find';
import {
  DeviceType,
  NEW_DEVICE_ID,
  NotificationDeviceTypeValues,
} from 'xm-constants/device';
import { AsYouType } from 'libphonenumber-js';

export { isFieldLocked } from '../../util';

export const COMPLIANCE_DIALOG_SHOWN = 'complianceDialogShown';

// List of the valid fax country codes
export const ALLOWED_FAX_COUNTRIES = [
  {
    name: 'Argentina',
    description: 'Argentine Republic',
    code2: 'AR',
    code3: 'ARG',
    dialCode: '54',
    continent: 'South America',
    language: 'Spanish',
  },
  {
    name: 'Australia',
    code2: 'AU',
    code3: 'AUS',
    dialCode: '61',
    continent: 'Australia',
    language: 'English',
  },
  {
    name: 'Brazil',
    description: 'Federative Republic of Brazil',
    code2: 'BR',
    code3: 'BRA',
    dialCode: '55',
    continent: 'South America',
    language: 'Portuguese',
  },
  {
    name: 'Canada',
    code2: 'CA',
    code3: 'CAN',
    dialCode: '1',
    continent: 'North America',
    language: 'English',
  },
  {
    name: 'France',
    description: 'French Republic',
    code2: 'FR',
    code3: 'FRA',
    dialCode: '33',
    continent: 'Europe',
    language: 'French',
  },
  {
    name: 'Germany',
    description: 'Federal Republic of Germany',
    code2: 'DE',
    code3: 'DEU',
    dialCode: '49',
    continent: 'Europe',
    language: 'German',
  },
  {
    name: 'Hong Kong',
    description: 'Hong Kong Special Administrative Region of China',
    code2: 'HK',
    code3: 'HKG',
    dialCode: '852',
    continent: 'Asia',
    language: 'Chinese',
  },
  {
    name: 'India',
    description: 'Republic of India',
    code2: 'IN',
    code3: 'IND',
    dialCode: '91',
    continent: 'Asia',
    language: 'English',
  },
  {
    name: 'Italy',
    description: 'Italian Republic',
    code2: 'IT',
    code3: 'ITA',
    dialCode: '39',
    continent: 'Europe',
    language: 'Italian',
  },
  {
    name: 'Japan',
    code2: 'JP',
    code3: 'JPN',
    dialCode: '81',
    continent: 'Asia',
    language: 'Japanese',
  },
  {
    name: 'Portugal',
    description: 'Portuguese Republic',
    code2: 'PT',
    code3: 'PRT',
    dialCode: '351',
    continent: 'Europe',
    language: 'Portuguese',
  },
  {
    name: 'Puerto Rico',
    code2: 'PR',
    code3: 'PRI',
    dialCode: '1',
    continent: 'North America',
    language: 'Spanish',
  },
  {
    name: 'United Kingdom',
    description: 'United Kingdom of Great Britain and Northern Ireland',
    code2: 'GB',
    code3: 'GBR',
    dialCode: '44',
    continent: 'Europe',
    language: 'English',
  },
  {
    name: 'United States',
    description: 'United States of America',
    code2: 'US',
    code3: 'USA',
    dialCode: '1',
    continent: 'North America',
    language: 'English',
  },
];
const COUNTRY_ISO_3166_ALPHA2_KEY = 'code2';
const COUNTRY_ISO_3166_ALPHA3_KEY = 'code3';
const phoneNumberRegex = new RegExp('[^0-9 ,-]', 'g');
const whitespaceAndDashesRegex = new RegExp('-| ', 'g');

export const DEFAULT_DELAY_MINUTES = 5;
export const defaultDeviceTimeframe = {
  count: 1,
  total: 1,
  data: [
    {
      name: '24x7',
      startTime: '00:00',
      timezone: 'US/Pacific',
      durationInMinutes: 1440,
      excludeHolidays: false,
      days: ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'],
    },
  ],
};

export const newDeviceConfig = {
  timeframes: defaultDeviceTimeframe,
  priorityThreshold: 'LOW',
  delay: 0,
  status: 'ACTIVE',
  defaultDevice: false,
  id: NEW_DEVICE_ID,
};

export const formFieldNames = {
  DEVICE_NAME: 'deviceName',
  PRIORITY: 'priority',
  NUMBER: 'number',
  CODE: 'code',
  EMAIL: 'email',
  DEVICE_DELAY: 'deviceDelay',
  PIN: 'pin',
  PROVIDER: 'provider',
  STATUS: 'status',
  DEFAULT_DEVICE: 'defaultDevice',
  TWO_WAY_DEVICE: 'twoWayDevice',
  EXTENSION: 'extension',
};

export const getCodeOptions = (
  countries,
  deviceType,
  isNewDevice,
  selectedVal,
) => {
  let validCountries = countries;
  const countryKey =
    isNewDevice && deviceType !== 'FAX'
      ? COUNTRY_ISO_3166_ALPHA3_KEY
      : COUNTRY_ISO_3166_ALPHA2_KEY;

  if (deviceType === 'FAX') {
    const faxCountryCodes = ALLOWED_FAX_COUNTRIES.map(
      country => country[countryKey],
    );

    validCountries =
      countries?.filter(country =>
        faxCountryCodes.includes(country[countryKey]),
      ) || [];
  }

  return validCountries?.map(
    country =>
      ({
        label: `${country.name} (+${country.dialCode})`,
        value: `+${country.dialCode}`,
        display: `+${country.dialCode} ${country.name} `,
        country: `${country[countryKey]}`,
        checked: selectedVal === `${country[countryKey]}`,
      }) || [],
  );
};

export const getDeviceCodes = (countries, country, isNewDevice, deviceType) => {
  if (countries) {
    if (deviceType === 'FAX') {
      const countryKey =
        country.length === 3
          ? COUNTRY_ISO_3166_ALPHA3_KEY
          : COUNTRY_ISO_3166_ALPHA2_KEY;
      const faxCountryCodes = ALLOWED_FAX_COUNTRIES.map(
        faxCountryCode => faxCountryCode[countryKey],
      );

      // is the country code actually allowed for fax?
      if (faxCountryCodes.includes(country)) {
        return ALLOWED_FAX_COUNTRIES.find(item => item[countryKey] === country);
      }

      // default to the first available allowed country
      return countries?.find(countryCode =>
        faxCountryCodes.includes(countryCode[countryKey]),
      );
    }
    return isNewDevice
      ? countries?.find(item => item[COUNTRY_ISO_3166_ALPHA3_KEY] === country)
      : countries?.find(item => item[COUNTRY_ISO_3166_ALPHA2_KEY] === country);
  }
  return '';
};

export const getNameOptions = (
  data,
  allNames,
  currentName,
  canManagePrivileged,
  intl,
) => {
  const remainingData = data?.filter(
    item => !allNames.includes(item.name) || item.name === currentName,
  );

  const getLabel = item => {
    const { name, privileged } = item;
    return privileged
      ? `${name} (${intl.formatMessage({ id: 'DEVICE_PRIVILEGED' })})`
      : name;
  };

  const options =
    remainingData?.map(item => ({
      label: getLabel(item),
      value: item.name,
      disabled: item.privileged && !canManagePrivileged,
      disabledTooltipId: 'CANNOT_ADD_PRIVILEGED',
    })) || [];
  return options;
};

export const getOnlyDigits = numberWithCharacters =>
  numberWithCharacters?.replace(/[^0-9]/g, '');

export const getPostBody = (prevValues, data, targetUserId, deviceType, id) => {
  const {
    number,
    code,
    email,
    priority,
    deviceName,
    deviceDelay,
    pin,
    provider,
    status,
    defaultDevice,
    twoWayDevice,
    extension,
  } = data;

  const body = {
    deviceType,
    owner: targetUserId,
  };
  let formValues = {};

  const unFormattedNumber = number ? getOnlyDigits(number) : '';

  if (deviceName) {
    formValues = {
      ...formValues,
      name: deviceName.value,
    };
  }
  if (priority) {
    formValues = {
      ...formValues,
      priorityThreshold: priority.value,
    };
  }

  if (email) {
    formValues = {
      ...formValues,
      emailAddress: email,
    };
  }
  if (deviceDelay) {
    formValues = {
      ...formValues,
      delay: deviceDelay,
    };
  }
  if (pin) {
    formValues = {
      ...formValues,
      pin,
    };
  }
  if (provider) {
    formValues = {
      ...formValues,
      provider: {
        id: provider,
      },
    };
  }
  if (!isUndefined(twoWayDevice)) {
    formValues = {
      ...formValues,
      twoWayDevice,
    };
  }
  if (!isUndefined(status)) {
    formValues = {
      ...formValues,
      status: status ? 'ACTIVE' : 'INACTIVE',
    };
  }
  if (!isUndefined(defaultDevice)) {
    formValues = {
      ...formValues,
      defaultDevice,
    };
  }
  if (deviceType === 'FAX' && code && number) {
    formValues = {
      ...formValues,
      country: `${code.country}`,
      phoneNumber: unFormattedNumber,
    };
  } else if (code && number && extension) {
    formValues = {
      ...formValues,
      phoneNumber: `${code.value}${unFormattedNumber};ext=${extension}`,
      country: `${code.country}`,
    };
  } else if (code && number) {
    formValues = {
      ...formValues,
      phoneNumber: `${code.value}${unFormattedNumber}`,
      country: `${code.country}`,
    };
  }

  Object.entries(formValues).forEach(([key, value]) => {
    if (!isEqual(prevValues[key], value) && !isUndefined(value)) {
      body[key] = value;
      // When the contact details of a device are edited
      // it should be setback to NOT TESTED
      if (
        key === 'emailAddress' ||
        key === 'phoneNumber' ||
        key === 'country' ||
        key === 'pin'
      ) {
        body.testStatus = 'UNTESTED';
      }
    }
  });

  return id === NEW_DEVICE_ID ? body : { ...body, id };
};

export const getSuccessMsg = postedData => {
  const { id, sequence } = postedData;
  if (!id) {
    return 'DEVICE_ADDED';
  }
  if (sequence) {
    return 'DEVICE_ORDER_UPDATED';
  }
  return 'DEVICE_UPDATED';
};

export const getPermittedDeviceTypes = (
  deviceTypes,
  isViewingOwnProfile,
  deviceTypesWithProviders,
) => {
  if (!deviceTypesWithProviders?.length || !deviceTypes?.length) {
    return [];
  }
  const list = deviceTypes.filter(type => {
    if (
      (type === DeviceType.ANDROID_PUSH ||
        type === DeviceType.APPLE_PUSH ||
        type === DeviceType.MS_TEAMS) &&
      !isViewingOwnProfile
    ) {
      return false;
    }
    return (
      NotificationDeviceTypeValues.includes(type) &&
      deviceTypesWithProviders.includes(type)
    );
  });
  return list;
};

const getCanAddRemainingDevices = (type, deviceNames) =>
  deviceNames.filter(
    ({ deviceType, privileged }) => deviceType === type && !privileged,
  ).length > 0;

export const getUnusedDeviceNames = (deviceNames, usedNames) =>
  deviceNames?.filter(item => !usedNames?.includes(item.name));
export const getUsedDeviceNames = (deviceNames, usedNames) =>
  deviceNames?.filter(item => usedNames?.includes(item.name));

export const getDeviceTypeStatus = (
  deviceNames,
  deviceTypes,
  usedNames,
  isViewingOwnProfile,
  canManagePrivileged,
  deviceTypesWithProviders,
) => {
  const deviceTypeStatus = {};
  const permittedDeviceTypes = getPermittedDeviceTypes(
    deviceTypes,
    isViewingOwnProfile,
    deviceTypesWithProviders,
  );
  const unUsedDeviceNames = getUnusedDeviceNames(deviceNames, usedNames);

  // device Types which can still be added
  const remainingDeviceTypes = unUsedDeviceNames.map(item => item.deviceType);
  const { hasOwnProperty } = Object.prototype;

  permittedDeviceTypes?.forEach(type => {
    let key = type;
    const canAddRemainingDevices =
      canManagePrivileged ||
      isViewingOwnProfile ||
      deviceTypeStatus[key]?.canAddRemainingDevices
        ? true
        : getCanAddRemainingDevices(type, unUsedDeviceNames);
    let hasDeviceTypeRemaining = remainingDeviceTypes?.includes(type);

    if (type === DeviceType.ANDROID_PUSH || type === DeviceType.APPLE_PUSH) {
      key = 'MOBILE_APP';
      // Mobile Apps are always allowed to be "added"
      hasDeviceTypeRemaining = true;
    } else if (type === DeviceType.MS_TEAMS || type === DeviceType.SLACK) {
      key = 'CHAT';
    }
    // Adding this check for grouped devices
    // for eg to only check for android if all
    // ios devices are in use
    if (
      !hasOwnProperty.call(deviceTypeStatus, key) ||
      !deviceTypeStatus[key].hasDeviceTypeRemaining
    ) {
      deviceTypeStatus[key] = {
        hasDeviceTypeRemaining,
        canAddRemainingDevices,
      };
    }
  });
  return deviceTypeStatus;
};

export const getMobileDeviceStatus = (
  deviceNames,
  deviceTypes,
  usedNames,
  isViewingOwnProfile,
  canManagePrivileged,
  deviceTypesWithProviders,
) => {
  const deviceTypeStatus = {};
  const permittedDeviceTypes = getPermittedDeviceTypes(
    deviceTypes,
    isViewingOwnProfile,
    deviceTypesWithProviders,
  );
  const unUsedDeviceNames = getUnusedDeviceNames(deviceNames, usedNames);
  const usedDeviceNames = getUsedDeviceNames(deviceNames, usedNames);
  const usedDeviceTypes = usedDeviceNames.map(item => item.deviceType);
  const hasMobileTypeUsed =
    usedDeviceTypes?.includes('APPLE_PUSH') ||
    usedDeviceTypes?.includes('ANDROID_PUSH');
  // device Types which can still be added
  const remainingDeviceTypes = unUsedDeviceNames.map(item => item.deviceType);
  const { hasOwnProperty } = Object.prototype;

  permittedDeviceTypes
    ?.filter(
      type =>
        type === DeviceType.ANDROID_PUSH || type === DeviceType.APPLE_PUSH,
    )
    .forEach(type => {
      // add only Mobile App Device Types
      const key = 'MOBILE_APP';
      const canAddRemainingDevices =
        canManagePrivileged ||
        isViewingOwnProfile ||
        deviceTypeStatus[key]?.canAddRemainingDevices
          ? true
          : getCanAddRemainingDevices(type, unUsedDeviceNames);
      const hasMobileTypeRemaining =
        remainingDeviceTypes?.includes('APPLE_PUSH') ||
        remainingDeviceTypes?.includes('ANDROID_PUSH');

      // Adding this check for grouped devices
      // for eg to only check for android if all
      // ios devices are in use
      if (
        !hasOwnProperty.call(deviceTypeStatus, key) ||
        !deviceTypeStatus[key].hasDeviceTypeRemaining
      ) {
        deviceTypeStatus[key] = {
          hasMobileTypeRemaining,
          hasMobileTypeUsed,
          usedDeviceTypes,
          canAddRemainingDevices,
        };
      }
    });
  return deviceTypeStatus;
};

export const hasMobile = devices =>
  !!find(
    devices,
    ({ deviceType }) =>
      deviceType === DeviceType.ANDROID_PUSH ||
      deviceType === DeviceType.APPLE_PUSH,
  );

export const isDeviceTypeEnabled = (deviceType, isOnFreePlan) => {
  if (isOnFreePlan) {
    return !(
      deviceType === DeviceType.VOICE ||
      deviceType === DeviceType.TEXT_PHONE ||
      deviceType === DeviceType.TEXT_PAGER
    );
  }
  return true;
};

export const isAddDeviceDisabled = (
  deviceType,
  isDevicesEnabled,
  isAddingDevice,
  isOnFreePlan,
  deviceTypeStatus,
) => {
  const { hasDeviceTypeRemaining, canAddRemainingDevices } =
    deviceTypeStatus[deviceType];
  if (isDevicesEnabled) {
    return (
      !hasDeviceTypeRemaining ||
      !canAddRemainingDevices ||
      !isDeviceTypeEnabled(deviceType, isOnFreePlan) ||
      isAddingDevice
    );
  }

  return (
    deviceTypeStatus[deviceType] ||
    !isDeviceTypeEnabled(deviceType, isOnFreePlan)
  );
};

export const getDisabledTooltip = (
  deviceType,
  isOnFreePlan,
  deviceTypeStatus,
  isNewlyUpgraded,
  isDevicesEnabled,
  isAddingDevice,
) => {
  let tooltipId;
  const hasDeviceTypeRemaining = isDevicesEnabled
    ? deviceTypeStatus[deviceType].hasDeviceTypeRemaining
    : !deviceTypeStatus[deviceType];
  const { canAddRemainingDevices } = deviceTypeStatus[deviceType];
  if (isAddingDevice) {
    tooltipId = 'DEVICE_SAVE_CHANGE';
  } else if (!isDeviceTypeEnabled(deviceType, isOnFreePlan)) {
    tooltipId = 'DEVICE_NEED_UPGRADE';
  } else if (!hasDeviceTypeRemaining) {
    tooltipId = 'ALL_DEVICES_ARE_IN_USE';
  } else if (isNewlyUpgraded) {
    tooltipId = 'UPGRADE_IN_PROGRESS_SHORT_MESSAGE';
  } else if (!canAddRemainingDevices) {
    tooltipId = 'DEVICE_NON_PRIVILEGED_AVAILABILITY';
  }

  return tooltipId;
};

export const getFormattedNumber = (code, number) => {
  return new AsYouType().input(`${code}${number}`).split(code)[1].trim();
};

export const getShowToast = (delay, showNoToastWithDelay) => {
  return !(showNoToastWithDelay && !isUndefined(delay));
};

export const getAlertInfo = (
  showExternalDeviceAlert,
  showPrivilegedDeviceAlert,
) => {
  const devicesInfo = {};
  if (showExternalDeviceAlert && showPrivilegedDeviceAlert) {
    devicesInfo.id = 'DEVICES_LOCKED_PRIVILEGED_INFO';
  } else if (showExternalDeviceAlert) {
    devicesInfo.id = 'DEVICE_EXTERNALLY_OWNED_INFO';
    devicesInfo.helpId = 'APIExternalOwnership';
  } else if (showPrivilegedDeviceAlert) {
    devicesInfo.id = 'DEVICE_PRIVILEGED_INFO';
    devicesInfo.helpId = 'PrivilegedDevices';
  }
  return devicesInfo;
};

export function showError(error, intl, addToast, setError, showToastError) {
  const { NUMBER, EMAIL } = formFieldNames;
  const errorJson = error.json;
  const subcode = errorJson?.subcode;

  if (
    subcode === 'validation.common.phonenumber.invalid' ||
    subcode === 'validation.device.common.cannot_parse_phone_number'
  ) {
    setError(NUMBER, {
      message: intl.formatMessage({
        id: 'DEVICES_INVALID_PHONE',
      }),
    });
  } else if (subcode === 'validation.common.faxnumber.invalid') {
    setError(NUMBER, {
      message: intl.formatMessage({
        id: 'DEVICES_INVALID_FAX',
      }),
    });
  } else if (subcode === 'validation.device.invalid_domain') {
    const domains = errorJson?.arguments[2];
    const numberOfDomains = domains?.split(',').length;
    setError(EMAIL, {
      message: intl.formatMessage(
        { id: 'DEVICES_INVALID_DOMAIN' },
        { number: numberOfDomains, domains },
      ),
    });
  } else if (showToastError) {
    addToast({
      type: 'error',
      message:
        error.json?.message ||
        intl.formatMessage({
          id: 'DEVICES_GENERIC_ERROR',
        }),
    });
  }
}

export const hasContactInfoUpdated = postedData => {
  const { pin, emailAddress, phoneNumber, country } = postedData;
  return pin || emailAddress || phoneNumber || country;
};

/**
 * Returns an empty string if phoneNumber
 * contains only whitespace or dashes.
 * This helps to allow validation to trigger,
 * which would otherwise not happen
 * as spaces and dashes are valid characters.
 *
 * Returns a phone number stripped of all invalid characters.
 *
 * @param {string} phoneNumber
 * @returns {string}
 */
export const sanitizePhoneNumber = phoneNumber => {
  const trimmed = phoneNumber.replace(whitespaceAndDashesRegex, '');
  const sanitized = trimmed.length
    ? phoneNumber.replace(phoneNumberRegex, '')
    : trimmed;
  return sanitized;
};
